import * as React from 'react';

export interface ButtonGroupProps {
  /**
   * Button children to group together
   */
  children: React.ReactNode;
  /**
   * Layout orientation of the button group
   * @default 'horizontal'
   */
  orientation?: 'horizontal' | 'vertical';
  /**
   * Whether buttons are visually connected with no gap
   * @default false
   */
  attached?: boolean;
  /**
   * Override size for all child buttons
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * ButtonGroup component for grouping related buttons together.
 *
 * Features:
 * - Horizontal and vertical orientation
 * - Attached mode with connected borders (no double borders)
 * - Detached mode with small gap between buttons
 * - Size override for all children
 * - Proper border-radius handling for attached mode
 * - Accessible with role="toolbar" for keyboard navigation
 */
const ButtonGroup = React.forwardRef<HTMLDivElement, ButtonGroupProps>(
  (
    {
      children,
      orientation = 'horizontal',
      attached = false,
      size,
      className = '',
    },
    ref
  ) => {
    const isHorizontal = orientation === 'horizontal';

    // Base container classes
    const baseClasses = [
      'inline-flex',
      isHorizontal ? 'flex-row' : 'flex-col',
    ].join(' ');

    // Gap classes for detached mode
    const gapClasses = attached ? '' : 'gap-1';

    // CSS classes for attached mode border-radius handling
    // These are applied via CSS adjacent sibling selectors
    const getAttachedClasses = (): string => {
      if (!attached) return '';
      if (isHorizontal) {
        return '[&>*:not(:first-child):not(:last-child)]:rounded-none [&>*:first-child]:rounded-r-none [&>*:last-child]:rounded-l-none [&>*:not(:first-child)]:-ml-px';
      }
      return '[&>*:not(:first-child):not(:last-child)]:rounded-none [&>*:first-child]:rounded-b-none [&>*:last-child]:rounded-t-none [&>*:not(:first-child)]:-mt-px';
    };
    const attachedClasses = getAttachedClasses();

    // Handle single child case for attached mode
    const singleChildClasses = attached ? '[&>*:only-child]:rounded-md [&>*:only-child]:ml-0 [&>*:only-child]:mt-0' : '';

    const combinedClasses = [
      baseClasses,
      gapClasses,
      attachedClasses,
      singleChildClasses,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    // Clone children to inject size prop if specified
    const enhancedChildren = size
      ? React.Children.map(children, (child) => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child as React.ReactElement<{ size?: 'sm' | 'md' | 'lg' }>, {
              size,
            });
          }
          return child;
        })
      : children;

    return (
      <div ref={ref} role="toolbar" aria-orientation={orientation} className={combinedClasses}>
        {enhancedChildren}
      </div>
    );
  }
);

ButtonGroup.displayName = 'ButtonGroup';

export { ButtonGroup };
